import React from 'react';
import { Document, Font, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { toBnNumber } from '../../../../../common/functions';
import kalpurushFont from '../../../../../assets/font/Kalpurush.ttf';

Font.register({
  family: 'Kalpurush',
  src: kalpurushFont,
});

Font.registerHyphenationCallback((word) => [word]);

const getStyles = (font, fontBold) =>
  StyleSheet.create({
    page: {
      padding: 18,
      fontSize: 8,
      color: '#000',
      fontFamily: font,
    },
    title: {
      fontSize: 14,
      fontFamily: fontBold,
      textAlign: 'center',
      marginBottom: 8,
    },
    filterBox: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 8,
      padding: 6,
      borderWidth: 1,
      borderColor: '#399737',
    },
    filterItem: {
      width: '25%',
      fontSize: 9,
      marginBottom: 2,
    },
    meta: {
      fontSize: 9,
      marginBottom: 8,
    },
    table: {
      width: '100%',
      borderWidth: 1,
      borderColor: '#999',
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#999',
      minHeight: 18,
      alignItems: 'center',
    },
    tableHeader: {
      backgroundColor: '#BFE3B5',
    },
    headerCell: {
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 4,
      paddingRight: 8,
      borderRightWidth: 1,
      borderRightColor: '#999',
      fontSize: 8,
      fontFamily: fontBold,
    },
    cell: {
      paddingTop: 3,
      paddingBottom: 3,
      paddingLeft: 4,
      paddingRight: 6,
      borderRightWidth: 1,
      borderRightColor: '#999',
      fontSize: 8,
    },
  });

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const localizeValue = (value, isBn) => {
  if (value === null || value === undefined || value === '') return '-';
  return isBn ? toBnNumber(value) : String(value);
};

const translateGender = (gender, intl) => {
  if (gender === 'Boy' || gender === 'Girl') {
    return intl.formatMessage({ id: gender });
  }
  return formatValue(gender);
};

const translateBmiStatus = (status, intl) => {
  const value = (status || '').toLowerCase();
  if (value.includes('severe')) return intl.formatMessage({ id: 'bmiSevereMalnourished' });
  if (value.includes('moderately')) return intl.formatMessage({ id: 'bmiModeratelyMalnourished' });
  if (value.includes('mild mal')) return intl.formatMessage({ id: 'bmiMildMalnourished' });
  if (value.includes('obesity') || value.includes('obese')) return intl.formatMessage({ id: 'bmiObesity' });
  if (value.includes('mild over')) return intl.formatMessage({ id: 'bmiMildOverweight' });
  if (value.includes('overweight')) return intl.formatMessage({ id: 'bmiOverweight' });
  if (value.includes('normal')) return intl.formatMessage({ id: 'bmiNormal' });
  return formatValue(status);
};

const studentHeight = (student, isBn, intl) => {
  const t = (id) => intl.formatMessage({ id });
  const parts = [
    student?.feet != null ? `${localizeValue(student.feet, isBn)} ${t('ftUnit')}` : '',
    student?.inch != null ? `${localizeValue(student.inch, isBn)} ${t('inUnit')}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const metric = student?.height_in_meter != null ? `${localizeValue(student.height_in_meter, isBn)} ${t('meterUnit')}` : '';
  if (parts && metric) return `${parts} (${metric})`;
  return parts || metric || '-';
};

const cellValue = (student, key, isBn, intl) => {
  if (key === 'height') return studentHeight(student, isBn, intl);
  if (key === 'weight') {
    return student.weight != null
      ? `${localizeValue(student.weight, isBn)} ${intl.formatMessage({ id: 'kgUnit' })}`
      : '-';
  }
  if (key === 'gender') return translateGender(student.gender, intl);
  if (key === 'bmi_status') return translateBmiStatus(student.bmi_status, intl);
  if (key === 'name') return formatValue(student.name);
  return localizeValue(student[key], isBn);
};

const BmiListPdfFile = ({ students = [], meta = {}, isBn = false, intl }) => {
  const font = isBn ? 'Kalpurush' : 'Helvetica';
  const fontBold = isBn ? 'Kalpurush' : 'Helvetica-Bold';
  const styles = getStyles(font, fontBold);
  const t = (id) => intl.formatMessage({ id });
  const cols = [
    { key: 'roll', label: t('rollNumber'), width: isBn ? '12%' : '9%' },
    { key: 'name', label: t('studentName'), width: isBn ? '13%' : '14%' },
    { key: 'gender', label: t('gender'), width: '8%' },
    { key: 'age', label: t('age'), width: '6%' },
    { key: 'height', label: t('height'), width: isBn ? '15%' : '16%' },
    { key: 'weight', label: t('weight'), width: '8%' },
    { key: 'bmi', label: t('bmi'), width: '7%' },
    { key: 'bmi_status', label: t('bmiType'), width: '16%' },
    { key: 'date', label: t('date'), width: '9%' },
    { key: 'round', label: t('round'), width: '6%' },
  ];

  return (
    <Document>
      <Page size='A4' orientation='landscape' style={styles.page}>
        <Text style={styles.title}>{meta.title || t('bmiParticipatedList')}</Text>
        <View style={styles.filterBox}>
          <Text style={styles.filterItem}>
            {t('class')} : {formatValue(meta.className)}
          </Text>
          <Text style={styles.filterItem}>
            {t('section')} : {formatValue(meta.sectionName)}
          </Text>
          <Text style={styles.filterItem}>
            {t('year')} : {localizeValue(meta.year, isBn)}
          </Text>
          <Text style={styles.filterItem}>
            {t('round')} : {localizeValue(meta.round, isBn)}
          </Text>
        </View>
        <Text style={styles.meta}>
          {t('totalStudent')} : {localizeValue(students.length, isBn)}
        </Text>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            {cols.map((col) => (
              <Text key={col.key} wrap={false} style={[styles.headerCell, { width: col.width }]}>
                {`${col.label}\u00A0`}
              </Text>
            ))}
          </View>
          {students.map((student, index) => (
            <View style={styles.tableRow} key={student.id || index}>
              {cols.map((col) => (
                <Text key={col.key} style={[styles.cell, { width: col.width }]}>
                  {cellValue(student, col.key, isBn, intl)}
                </Text>
              ))}
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default BmiListPdfFile;
